import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/react';
import { appInfo } from '~/config/app';
import {
  createTracesSampler,
  getReplaysSessionSampleRate,
  IGNORED_RESOURCE_SPANS,
  REPLAYS_ON_ERROR_SAMPLE_RATE,
  shouldCreateSpanForRequest,
} from '~/config/sentry/sentry-config';
import '~/domain/legal/utils/init-consent';
import store from '~/store/Store';
import AppContainer from '~/components/containers/AppContainer/AppContainer';
import '~/app/emit-service-worker';
import 'normalize.css';
import './index.pcss';
import '~uikit/style.pcss';
import 'tailwindcss/utilities.css';
import 'tailwindcss/components.css';

import { registerServiceWorker } from '~/utils/service-worker-utils/service-worker-utils';
import { installServiceWorkerAuthRefreshListener } from '~/domain/auth/service-worker-auth-refresh';
import { initInternationalization } from '~/config/i18n/internationalization';
import getDefaultLocale from '~/utils/get-default-locale/get-default-locale';
import mapBrowserLocaleToFleetLocale from '~/utils/map-browser-locale-to-fleet-locale/map-browser-locale-to-fleet-locale';
import { installMapsAuthFailureHandler } from '~/domain/maps/utils/maps-auth-failure';
import { setNativeLogGate } from '~/utils/logger/logger';

setNativeLogGate(() => Boolean(store.getState()?.native?.onWebViewInitialized));

// If SENTRYIO_DSN is defined in the environment variables
// then we should initilize it.
if (process.env.SENTRYIO_DSN) {
  Sentry.init({
    dsn: process.env.SENTRYIO_DSN,
    environment: appInfo.appEnv,
    release: appInfo.webVersion,
    integrations: [
      Sentry.browserTracingIntegration({
        shouldCreateSpanForRequest,
        ignoreResourceSpans: IGNORED_RESOURCE_SPANS,
      }),
      Sentry.replayIntegration(),
    ],
    tracesSampler: createTracesSampler(appInfo.appEnv),
    replaysSessionSampleRate: getReplaysSessionSampleRate(appInfo.appEnv),
    replaysOnErrorSampleRate: REPLAYS_ON_ERROR_SAMPLE_RATE,
    sendDefaultPii: true,
    enabled: appInfo.appEnv !== 'dev-local',
  });
}

installMapsAuthFailureHandler();

void initInternationalization(
  mapBrowserLocaleToFleetLocale(navigator.language) ?? getDefaultLocale()
);

// Register early, without blocking render.
void registerServiceWorker();
installServiceWorkerAuthRefreshListener(store);

const container = document.getElementById('app');
if (!container) {
  throw new Error('Missing app root element');
}

const root = ReactDOM.createRoot(container);
root.render(
  <Provider store={store}>
    <AppContainer />
  </Provider>
);
